// Typed client for the public /webinare endpoints. Same-origin fetch, no
// dependency beyond the shared Supabase client (used only to attach the
// viewer's bearer token to the recording-access check).
import { supabase } from '../lib/supabase';

// ---------------------------------------------------------------------------
// Types (mirror api/events.ts, api/event-access.ts response shapes)
// ---------------------------------------------------------------------------
export type EventStatus = 'announced' | 'live' | 'ended';
export type RecordingAccess = 'free' | 'members' | 'paid';

export interface PublicEvent {
  slug: string;
  title: string;
  description: string;
  starts_at: string;
  duration_min: number;
  status: EventStatus;
  recording_access: RecordingAccess;
  price_cents: number | null;
  currency: string;
}

export interface EventsResponse {
  upcoming: PublicEvent[];
  recorded: PublicEvent[];
}

export type AccessDenyReason = 'login_required' | 'members_only' | 'purchase_required' | 'not_available';

export interface AccessResult {
  canView: boolean;
  status: EventStatus;
  videoId: string | null;
  reason?: AccessDenyReason;
}

export interface SubscribePayload {
  email: string;
  consent: boolean;
  website: string;
  source: string;
  event_slug?: string;
}

export interface SubscribeResult {
  ok: boolean;
  message: string;
}

export interface CheckoutResult {
  sessionId: string;
  url: string;
}

export interface LoginLinkResult {
  success: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Best-effort error message from a non-2xx JSON error body, else a generic fallback. */
async function extractError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body?.error === 'string') return body.error;
  } catch {
    // non-JSON (or empty) error body — fall through to the generic message
  }
  return `Request failed (${res.status})`;
}

/** GET/POST helper for endpoints whose failure responses do NOT match the success shape. */
async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(await extractError(res));
  return (await res.json()) as T;
}

function jsonHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return { 'Content-Type': 'application/json', ...extra };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function fetchEvents(): Promise<EventsResponse> {
  return request<EventsResponse>('/api/events');
}

export async function fetchAccess(slug: string): Promise<AccessResult> {
  const token = (await supabase?.auth.getSession())?.data.session?.access_token;
  const headers = jsonHeaders(token ? { Authorization: `Bearer ${token}` } : {});
  return request<AccessResult>('/api/event-access', {
    method: 'POST',
    headers,
    body: JSON.stringify({ slug }),
  });
}

// subscribe/send-login-link always answer with a well-formed body matching
// their result shape (even on validation failure), so callers branch on
// `.ok`/`.success` rather than on HTTP status.
export async function subscribe(payload: SubscribePayload): Promise<SubscribeResult> {
  const res = await fetch('/api/subscribe', {
    method: 'POST',
    headers: jsonHeaders({ Accept: 'application/json' }),
    body: JSON.stringify(payload),
  });
  return (await res.json()) as SubscribeResult;
}

export async function startCheckout(slug: string): Promise<CheckoutResult> {
  return request<CheckoutResult>('/api/create-checkout-session', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ slug }),
  });
}

export async function sendLoginLink(email: string, redirectTo: string): Promise<LoginLinkResult> {
  const res = await fetch('/api/send-login-link', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, redirect_to: redirectTo }),
  });
  return (await res.json()) as LoginLinkResult;
}
